import React from "react";
import PropTypes from "prop-types";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import {
  CONNECTION_STATUS_LABELS,
  STATUS_IDS,
  ACTION_IDS,
  statusIsEditable,
  TableEmptyMessage,
  STATUS_LABELS,
  isSet,
} from "../../../../components/Utils";
import { DeviceFormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import ANPConnectionForm from "./ANPConnectionForm";
import {
  PermissionResource,
  PermissionSection,
  Resources,
} from "../../../Permissions";

function areRolesSame(listA, listB) {
  if (listA.length !== listB.length) {
    return false;
  }
  const sortedA = [...listA].sort();
  const sortedB = [...listB].sort();
  for (let i = 0; i < sortedA.length; i++) {
    if (sortedA[i] !== sortedB[i]) {
      return false;
    }
  }
  return true;
}

function ANPConnectionListForm({
  forwardedRef,
  className = "",
  disabled: isDisabled = false,
  model,
  onChange,
  getCompanyData,
  roleList,
  readOnly,
  isNGUMActive,
}) {
  const formRef = React.useRef(null);

  const [state, setState] = React.useState({
    items: (model ? [model] : []).map((item, index) => ({
      ...item,
      roles: item.roles || [],
      rolesData: item.rolesData || [],
      index,
    })),
    localStateChanged: false,
  });

  const getModel = React.useCallback(() => {
    const { items } = state;
    return items[0];
  }, [state]);
  React.useImperativeHandle(forwardedRef, () => ({
    getModel,
  }));

  const onSave = React.useCallback(
    (onHide, updating) => {
      const currentModel = formRef.current.getModel();
      if (!currentModel) {
        return;
      }
      currentModel.status_id = updating ? STATUS_IDS.UPDATING : STATUS_IDS.NEW;
      currentModel.action_id = updating ? ACTION_IDS.UPDATE : ACTION_IDS.CREATE;
      currentModel.status = CONNECTION_STATUS_LABELS[currentModel.status_id];
      const { items } = state;
      let localStateChanged = false;
      if (
        currentModel.index !== undefined &&
        items[currentModel.index] !== undefined
      ) {
        // ensure that there were changes before updating action_id and status_id
        if (
          model &&
          model.roles &&
          areRolesSame(model.roles, currentModel.roles)
        ) {
          currentModel.action_id = model.action_id;
          currentModel.status_id = model.status_id;
          currentModel.status = model.status;
        } else {
          // set localStateChanged to true only if there is a remote version
          localStateChanged = !model || !model.id ? false : true;
          // console.log("<< onSave - localStateChanged", localStateChanged, model);
        }
        items[currentModel.index] = currentModel;
      } else {
        currentModel.index = items.length;
        items.push(currentModel);
      }
      // console.log("<< onSave final ", items);
      setState((prev) => ({ ...prev, items, localStateChanged }));
      onHide && onHide();
    },
    [state, model],
  );

  const onDelete = React.useCallback(
    (selected) => {
      // console.log("deleting", selected)
      if (selected.id !== undefined && selected.id !== null) {
        const { items } = state;
        // mark for deletion
        if (items[selected.index]) {
          items[selected.index].status_id = STATUS_IDS.DELETING;
          items[selected.index].action_id = ACTION_IDS.DELETE;
          items[selected.index].status = null;
          if (model && model.rolesData) {
            items[selected.index].rolesData = model.rolesData.map((role) => ({
              ...role,
              status_id: STATUS_IDS.DELETING,
              action_id: ACTION_IDS.DELETE,
            }));
          }
          // items[selected.index].rolesData = items[selected.index].rolesData.map(role => ({
          //   ...role,
          //   status_id: STATUS_IDS.DELETING,
          //   action_id: ACTION_IDS.DELETE,
          // }));
          setState((prev) => ({ ...prev, items, localStateChanged: true }));
          onChange && onChange(items);
        } else {
          console.error(
            "<< an-portal account to be deleted was not found",
            this.state.items,
          );
        }
      } else {
        // delete local changes quietly
        const items = state.items.filter((_, i) => i !== selected.index);
        setState((prev) => ({ ...prev, items }));
        onChange && onChange(false);
      }
    },
    [state, model, onChange],
  );

  const disabled = React.useMemo(
    () => isDisabled || readOnly,
    [isDisabled, readOnly],
  );
  console.log(
    "ANPConnectionListForm disabled:",
    disabled,
    isDisabled,
    readOnly,
  );

  const renderItemTable = React.useCallback(
    (items, actionTemplate) => {
      const originalModel = model ? [model] : [];
      const data = items.map((item, index) => ({
        ...item,
        index,
        status: STATUS_LABELS[item.status_id || STATUS_IDS.NEW],
      }));
      const columnStyle = {
        verticalAlign: "top",
        paddingTop: "16px",
        paddingBottom: "16px",
      };
      return (
        <>
          <DataTable
            value={originalModel}
            rows={1}
            emptyMessage={
              <TableEmptyMessage
                prefix="Kein"
                itemNamePlural="AN-Portal Zugang"
                min
              />
            }
          >
            <Column
              field="username"
              header="Benutzername"
              style={columnStyle}
              body={(item) => {
                if (isNGUMActive) {
                  return item.ngum_status_id !== 0 ? item.username : "";
                }
                return item.username;
              }}
            />
            <Column
              field="ngumStatus"
              header="Status Account"
              style={columnStyle}
            />
            <Column
              field="roles"
              header="Aktive Rollen"
              style={columnStyle}
              body={(item) => (
                <>
                  {(item.rolesData || [])
                    .filter(
                      (role) => role.ngum_status_id === STATUS_IDS.FINISHED,
                    )
                    .map((role) => (
                      <label key={role.name} className="w3-block">
                        {role.name}
                      </label>
                    ))}
                </>
              )}
            />
          </DataTable>
          <DataTable
            className="margin-big-top"
            value={data}
            rows={1}
            emptyMessage={
              <TableEmptyMessage
                prefix="Kein"
                itemNamePlural="AN-Portal Zugang"
                min
              />
            }
          >
            <Column
              field="roles"
              header="Beauftragung"
              style={columnStyle}
              body={(item) => {
                // let roles = [];
                // if (item.froze_requested_roles) {
                //   try {
                //     roles = JSON.parse(item.froze_requested_roles);
                //     console.log("Parsed froze_requested_roles:", roles);
                //   } catch (error) {
                //     console.error("Error parsing froze_requested_roles:", error);
                //   }
                // }
                // if (roles.length === 0) {
                //   roles = item.rolesData.filter(
                //     role => role.action_id !== 0 || (role.status_id === STATUS_IDS.FINISHED && role.ngum_status_id !== STATUS_IDS.FINISHED)
                //   );
                // }
                const roles = item.rolesData.filter(
                  (role) =>
                    role.action_id !== 0 ||
                    (role.status_id === STATUS_IDS.FINISHED &&
                      role.ngum_status_id !== STATUS_IDS.FINISHED),
                );
                const getStatusLabel = (statusId) => {
                  return statusId === STATUS_IDS.DELETING
                    ? "Löschung"
                    : STATUS_LABELS[statusId] || "Unbekannt";
                };
                return (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.25em",
                    }}
                  >
                    {roles.map((role) => (
                      <div
                        key={role.name}
                        className="w3-block"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <div className="pad-sm-tb- pad-lr">{role.name}</div>
                        <div
                          className="pad-sm-tb- pad-lr w3-light-grey"
                          style={{ width: "40%" }}
                        >
                          {getStatusLabel(role.status_id)}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              }}
            />
            <Column
              field="status"
              header="Status Bearbeitung"
              style={columnStyle}
            />
            <Column
              body={actionTemplate}
              style={{
                ...columnStyle /* , textAlign: "center", width: "8em" */,
              }}
            />
          </DataTable>
        </>
      );
    },
    [model],
  );

  const isEditDisabled = React.useCallback(
    (item) => {
      if (
        state.localStateChanged ||
        !isSet(item.id) ||
        item.status_id !== model.status_id
      ) {
        return false;
      }
      return !statusIsEditable(item.status_id, item.id) || disabled;
    },
    [state, disabled, model],
  );

  const renderItemForm = React.useCallback(
    (item, onHide) => {
      const connection = item || { status_id: STATUS_IDS.NEW };
      const saveDisabled = connection.status_id !== STATUS_IDS.NEW;
      const updateDisabled =
        !isSet(connection.id) || isEditDisabled(connection); //connection.status_id !== STATUS_IDS.FINISHED && !state.localStateChanged/* && !model.id */;
      const companyData = getCompanyData();
      if (companyData && !companyData.roleList) {
        companyData.roleList = roleList;
      }
      // // filter roles
      // if (connection.id) {
      //   connection.rolesData = (connection.rolesData || []).filter(role => role.ngum_status_id === STATUS_IDS.FINISHED);
      //   connection.roles = connection.rolesData.map(role => role.name);
      // }
      return (
        <>
          <ANPConnectionForm
            key={Math.random()}
            ref={formRef}
            model={connection}
            activeRolesData={model && model.rolesData ? model.rolesData : []}
            companyData={companyData}
            disabled={disabled}
          />
          <DeviceFormMenu
            className="margin-big-top w3-border-top pad-big w3-light-grey"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onUpdate={() => onSave(onHide, true)}
            saveDisabled={saveDisabled}
            saveHidden={disabled || saveDisabled}
            saveBtnLabel="Beantragen"
            saveBtnName="saveANPAccBtn"
            updateDisabled={updateDisabled}
            updateHidden={disabled || updateDisabled}
            updateBtnName="updateANPAccBtn"
            updateBtnLabel="Ändern"
            onCancel={onHide}
            cancelBtnLabel={
              disabled || (updateDisabled && saveDisabled)
                ? "Schließen"
                : undefined
            }
            cancelBtnName="cancelANPAccBtn"
          />
        </>
      );
    },
    [model, state, roleList, disabled, getCompanyData, onSave, isEditDisabled],
  );

  const isDeleteDisabled = React.useCallback(
    (item) => {
      if (!isSet(item.id) || item.status_id !== model.status_id) {
        return false;
      }
      return !statusIsEditable(item.status_id, item.id) || disabled;
    },
    [state, disabled, model],
  );

  const getItemFormTitle = React.useCallback(
    (connection) => {
      let action = "bearbeiten";
      if (!connection) {
        action = "anlegen";
      } else if (isEditDisabled(connection)) {
        action = "ansehen";
      }
      return <h3 className="no-margin">{`AN-Portal Zugang ${action}`}</h3>;
    },
    [isEditDisabled],
  );

  return (
    <ItemListManager
      key={Math.random()}
      itemPanelStyle={{ width: "800px" }}
      className={className}
      title={
        <h4 className="no-margin-top" style={{ height: "36px" }}>
          {"AN-Portal Zugang"}
        </h4>
      }
      addBtnLabel="Hinzufügen"
      addBtnName="addANPConnectionBtn"
      addBtnDisabled={disabled}
      addBtnHidden={
        state.items.length !== 0 ||
        (window && window.ANP_CREATION_FREEZE === "YES")
      }
      items={state.items}
      getItemFormTitle={getItemFormTitle}
      renderItemTable={renderItemTable}
      renderItemForm={renderItemForm}
      namePrefix="anpConnection"
      isEditDisabled={isEditDisabled}
      hideDisabledEdit
      itemDeleteable={!disabled}
      hideDisabledDelete
      isDeleteDisabled={isDeleteDisabled}
      deleteTitle="AN-Portal Zugang löschen"
      getDeleteText={(item) => {
        if (item.id === undefined || item.id === null) {
          return `Wollen Sie wirklich löschen?`;
        }
        return `Wollen Sie eine Löschung des AN-Portal Zugangs wirklich beantragen?`;
      }}
      onDelete={onDelete}
    />
  );
}

ANPConnectionListForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({
    username: PropTypes.string,
    roles: PropTypes.arrayOf(PropTypes.string),
    rolesData: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        status_id: PropTypes.number,
        action_id: PropTypes.number,
      }),
    ),
    status: PropTypes.string,
    status_id: PropTypes.number,
    action_id: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  isVodafoneAccountPresent: PropTypes.bool,
  roleList: PropTypes.array.isRequired,
  getCompanyData: PropTypes.func.isRequired,
  isNGUMActive: PropTypes.bool,
};

export default React.forwardRef((props, ref) => (
  <ANPConnectionListForm {...props} forwardedRef={ref} />
));

export const ANPConnectionPanel = ({ anpConnection }) => {
  // console.log("ADConnectionPanel", anpConnection);
  const [first, setFirst] = React.useState(0);
  const onPage = (event) => {
    setFirst(event.first);
  };
  const data = [];
  if (anpConnection) {
    data.push({
      ...anpConnection,
      status:
        anpConnection.status ||
        CONNECTION_STATUS_LABELS[anpConnection.status_id],
    });
  }

  const columnStyle = {
    verticalAlign: "top",
    paddingTop: "16px",
    paddingBottom: "16px",
  };
  return (
    <PermissionResource resource={Resources.Profile.ANPAccount}>
      <PermissionSection
        height="auto"
        id="anp-zugang"
        className="w3-border-top neg-margin-big-lr pad-big-lr pad-big-btm"
        isUnterWizardTitle
        idOffset={-90}
      >
        <h4 className="no-margin pad-big-top pad-btm">{"AN-Portal Account"}</h4>
        <DataTable
          value={data}
          rows={1}
          first={first}
          onPage={onPage}
          emptyMessage={
            <TableEmptyMessage
              prefix="Kein"
              itemNamePlural="AN-Portal Zugang"
              min
            />
          }
        >
          <Column field="username" header="Benutzername" style={columnStyle} />
          <Column
            field="roles"
            header="AN-Portal Rolle"
            style={columnStyle}
            body={(item) => (
              <>
                {item.roles.map((role) => (
                  <label key={role} className="w3-block">
                    {role}
                  </label>
                ))}
              </>
            )}
          />
          <Column field="status" header="Status" style={columnStyle} />
        </DataTable>
      </PermissionSection>
    </PermissionResource>
  );
};
